import React, { useEffect } from "react";

interface ShortcutEntry { action: string; keys: string[]; }
interface ShortcutGroup  { title: string; shortcuts: ShortcutEntry[]; }

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "Global",
    shortcuts: [
      { action: "Open command palette",     keys: ["Ctrl", "K"] },
      { action: "Show shortcuts reference", keys: ["Ctrl", "/"] },
      { action: "Open training guide",      keys: ["?"] },
    ],
  },
  {
    title: "Navigation (G-chord)",
    shortcuts: [
      { action: "Go to Dashboard",   keys: ["G", "D"] },
      { action: "Go to CRM",         keys: ["G", "C"] },
      { action: "Go to WhatsApp",    keys: ["G", "W"] },
      { action: "Go to Inventory",   keys: ["G", "I"] },
      { action: "Go to Pricing",     keys: ["G", "P"] },
      { action: "Go to Marketplace", keys: ["G", "M"] },
      { action: "Go to Attendance",  keys: ["G", "A"] },
    ],
  },
  {
    title: "CRM",
    shortcuts: [
      { action: "New lead",            keys: ["N"] },
      { action: "Focus search",        keys: ["F"] },
      { action: "Filter by stage",     keys: ["1–6"] },
      { action: "Open selected lead",  keys: ["Enter"] },
      { action: "Save form",           keys: ["Ctrl", "Enter"] },
      { action: "Close modal",         keys: ["Esc"] },
    ],
  },
  {
    title: "Training Guide",
    shortcuts: [
      { action: "Open guide",    keys: ["?"] },
      { action: "Close guide",   keys: ["Esc"] },
    ],
  },
];

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd style={{
    display: "inline-block",
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderBottom: "2px solid rgba(0,0,0,0.3)",
    borderRadius: 5, padding: "2px 7px",
    fontSize: 11, color: "#ffd6a5",
    fontFamily: "monospace", lineHeight: 1.4,
  }}>{children}</kbd>
);

interface ShortcutsModalProps { isOpen: boolean; onClose: () => void; }

const ShortcutsModal: React.FC<ShortcutsModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1100, backdropFilter: "blur(3px)" }} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(640px, 92vw)", maxHeight: "80vh",
        background: "linear-gradient(180deg, #111827 0%, #0b1326 100%)",
        border: "1px solid rgba(255,214,165,0.18)", borderRadius: 16,
        zIndex: 1101, overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 40px 100px rgba(0,0,0,0.8)",
        fontFamily: "'Manrope', 'Space Grotesk', sans-serif",
        animation: "scFadeIn 0.15s ease-out",
      }}>
        <style>{`@keyframes scFadeIn { from { opacity:0; transform:translate(-50%,-50%) scale(0.96); } to { opacity:1; transform:translate(-50%,-50%) scale(1); } }`}</style>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px 14px", borderBottom: "1px solid rgba(255,214,165,0.1)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>⌨️</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#ffd6a5" }}>Keyboard Shortcuts</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "14px 22px 22px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
            {SHORTCUT_GROUPS.map(group => (
              <div key={group.title}>
                <div style={{ fontSize: 10, color: "#ffd6a5", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid rgba(255,214,165,0.1)" }}>
                  {group.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {group.shortcuts.map(s => (
                    <div key={s.action} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 12, color: "#9b8f83", flex: 1 }}>{s.action}</span>
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        {s.keys.map((k, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <span style={{ color: "#4a5568", fontSize: 11, alignSelf: "center" }}>+</span>}
                            <Kbd>{k}</Kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: "10px 22px", fontSize: 11, color: "#4a5568", borderTop: "1px solid rgba(255,255,255,0.05)", textAlign: "center", flexShrink: 0 }}>
          Press <Kbd>Esc</Kbd> to close · <Kbd>Ctrl+K</Kbd> for command palette
        </div>
      </div>
    </>
  );
};

export default ShortcutsModal;
